/**
 * Клиент Go-API. В разработке запросы идут через прокси Vite на :8080,
 * в проде базовый адрес задаётся переменной VITE_API_URL.
 */

const BASE = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(message, status, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details || null;
    this.field = details?.field || null;
  }
}

/** Абсолютный адрес картинки товара с учётом базового адреса API. */
export const imageUrl = (src) => (src && src.startsWith('/') ? BASE + src : src || '');

/*
Секрет заказа.

Номер заказа человекочитаемый и потому угадываемый, поэтому сервер отдаёт заказ
только владельцу аккаунта или предъявителю секретного токена. Токен выдаётся
при оформлении и живёт здесь же, в браузере покупателя, — чтобы страница заказа
открывалась и без входа.
*/
const TOKENS_KEY = 'saints.orderTokens';

const readTokens = () => {
  try {
    return JSON.parse(localStorage.getItem(TOKENS_KEY)) || {};
  } catch {
    return {};
  }
};

export const rememberOrderToken = (number, token) => {
  if (!number || !token) return;
  try {
    const all = readTokens();
    all[number] = token;
    localStorage.setItem(TOKENS_KEY, JSON.stringify(all));
  } catch {
    /* приватный режим — переживём, владельцу заказ откроется по входу */
  }
};

export const orderToken = (number) => readTokens()[number] || '';

async function request(method, path, body, { signal, raw, orderNumber } = {}) {
  const secret = orderNumber ? orderToken(orderNumber) : '';
  const headers = {};
  if (!raw && body !== undefined) headers['Content-Type'] = 'application/json';
  if (secret) headers['X-Order-Token'] = secret;

  let res;
  try {
    res = await fetch(BASE + path, {
      method,
      credentials: 'include',
      headers: Object.keys(headers).length ? headers : undefined,
      body: raw ?? (body === undefined ? undefined : JSON.stringify(body)),
      signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new ApiError('Нет связи с сервером. Проверьте, что запущен Go-бекенд.', 0);
  }

  let data = null;
  try {
    data = await res.json();
  } catch {
    /* пустое тело — допустимо */
  }

  if (!res.ok || data?.ok === false) {
    throw new ApiError(data?.error || `Ошибка ${res.status}`, res.status, data?.details);
  }
  return data ?? {};
}

const qs = (params) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) {
    if (v !== null && v !== undefined && v !== '') s.set(k, v);
  }
  const str = s.toString();
  return str ? `?${str}` : '';
};

export const api = {
  /* Витрина */
  bootstrap: () => request('GET', '/api/bootstrap'),
  products: (params) => request('GET', `/api/products${qs(params)}`),
  product: (slug) => request('GET', `/api/products/${encodeURIComponent(slug)}`),
  categories: () => request('GET', '/api/categories'),

  // OneMerch: оформление уходит на их чекаут, заказ возвращается вебхуком
  onemerch: {
    checkout: (payload) => request('POST', '/api/checkout/onemerch', payload),
    order: (number) => request('GET', `/api/onemerch/orders/${encodeURIComponent(number)}`),
  },
  shipping: () => request('GET', '/api/shipping'),
  cdekPoints: (params) => request('GET', `/api/cdek/points${qs(params)}`),

  /* Отзывы */
  reviews: (params) => request('GET', `/api/reviews${qs(params)}`),
  addReview: (slug, payload) => request('POST', `/api/products/${encodeURIComponent(slug)}/reviews`, payload),

  /* Корзина */
  cart: () => request('GET', '/api/cart'),
  cartAdd: (payload) => request('POST', '/api/cart/items', payload),
  cartUpdate: (id, qty) => request('PATCH', `/api/cart/items/${id}`, { qty }),
  cartRemove: (id) => request('DELETE', `/api/cart/items/${id}`),
  cartClear: () => request('POST', '/api/cart/clear', {}),
  cartPromo: (code) => request('POST', '/api/cart/promo', { code }),
  cartShipping: (code) => request('POST', '/api/cart/shipping', { code }),

  /* Аккаунт */
  me: () => request('GET', '/api/auth/me'),
  login: (payload) => request('POST', '/api/auth/login', payload),
  register: (payload) => request('POST', '/api/auth/register', payload),
  logout: () => request('POST', '/api/auth/logout', {}),
  updateMe: (payload) => request('PATCH', '/api/auth/me', payload),
  changePassword: (payload) => request('POST', '/api/auth/password', payload),
  sendEmailCode: () => request('POST', '/api/auth/email/send', {}),
  confirmEmailCode: (code) => request('POST', '/api/auth/email/confirm', { code }),

  /* Заказы */
  createOrder: (payload) => request('POST', '/api/orders', payload),
  order: (number) => request('GET', `/api/orders/${encodeURIComponent(number)}`, undefined, { orderNumber: number }),
  myOrders: () => request('GET', '/api/orders'),
  cancelOrder: (number) => request('POST', `/api/orders/${encodeURIComponent(number)}/cancel`, {}),
  repeatOrder: (number) => request('POST', `/api/orders/${encodeURIComponent(number)}/repeat`, {}),
  paymentState: (number) =>
    request('GET', `/api/orders/${encodeURIComponent(number)}/payment`, undefined, { orderNumber: number }),
  payOrder: (number) => request('POST', `/api/orders/${encodeURIComponent(number)}/pay`, {}, { orderNumber: number }),

  /* Админка */
  admin: {
    stats: () => request('GET', '/api/admin/stats'),

    products: (params) => request('GET', `/api/admin/products${qs(params)}`),
    product: (id) => request('GET', `/api/admin/products/${id}`),
    createProduct: (payload) => request('POST', '/api/admin/products', payload),
    updateProduct: (id, payload) => request('PATCH', `/api/admin/products/${id}`, payload),
    deleteProduct: (id) => request('DELETE', `/api/admin/products/${id}`),

    categories: () => request('GET', '/api/admin/categories'),
    onemerchSync: () => request('POST', '/api/admin/onemerch/sync'),
    onemerchPull: () => request('POST', '/api/admin/onemerch/pull'),
    createCategory: (payload) => request('POST', '/api/admin/categories', payload),
    deleteCategory: (slug) => request('DELETE', `/api/admin/categories/${encodeURIComponent(slug)}`),

    orders: (params) => request('GET', `/api/admin/orders${qs(params)}`),
    setOrderStatus: (id, status) => request('PATCH', `/api/admin/orders/${id}`, { status }),
    setOrderTracking: (id, tracking) => request('PUT', `/api/admin/orders/${id}/tracking`, { tracking }),

    users: (params) => request('GET', `/api/admin/users${qs(params)}`),
    setUserRole: (id, role) => request('PATCH', `/api/admin/users/${id}`, { role }),

    reviews: (params) => request('GET', `/api/admin/reviews${qs(params)}`),
    updateReview: (id, payload) => request('PATCH', `/api/admin/reviews/${id}`, payload),
    deleteReview: (id) => request('DELETE', `/api/admin/reviews/${id}`),

    promos: () => request('GET', '/api/admin/promos'),
    savePromo: (payload) => request('PUT', '/api/admin/promos', payload),
    deletePromo: (code) => request('DELETE', `/api/admin/promos/${encodeURIComponent(code)}`),

    upload: (file) => {
      const form = new FormData();
      form.append('file', file);
      return request('POST', '/api/admin/upload', undefined, { raw: form });
    },
  },
};

export default api;
